import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonDatagrid } from '..';
import { getProductsColumns } from './columns';
import { IProduct, IProductAttachment } from '../../../types/product';
import { Box, Button } from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { ProductsService } from '../../../api/services/productsService';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../../types/store';
import { setProducts, setProductSyncStatus } from '../../../store/Reducers/ProductsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { showAlertDialog } from '../../../store/Reducers/AlertDialogSlice';
import { SyncStatusCard } from '../../SyncStatusCard';
import { Pagination } from 'swiper/modules';

export const ProductDatagrid = () => {
  const [viewedProduct, setViewedProduct] = useState<IProduct | null>(null);
  const { page, content: data, syncStatus } = useSelector((state: IRootState) => state.products);

  const dispatch = useDispatch();

  const forceSyncSku = useCallback(
    async (sku: string) => {
      try {
        const res = await ProductsService.forceSync([sku]);
        const failedOrMissing = [...res.not_found, ...res.failed];
        dispatch(
          showNotification({
            message:
              failedOrMissing.length > 0
                ? `Force sync queued for ${sku}, but could not be fetched from Cin7.`
                : `Force sync queued for ${sku}.`,
            severity: failedOrMissing.length > 0 ? 'warning' : 'success',
          }),
        );
      } catch (e: any) {
        dispatch(
          showNotification({
            message: `Failed to force sync ${sku}. ${e?.response?.data?.message || ''}`,
            severity: 'error',
          }),
        );
      }
    },
    [dispatch],
  );

  const onClickForceSync = useCallback(
    (sku: string) => {
      dispatch(
        showAlertDialog({
          title: `Force sync ${sku}?`,
          description:
            'This re-syncs the product from Cin7 and re-runs image/index processing, even if nothing appears to have changed.',
          confirmText: 'Force Sync',
          cancelText: 'Cancel',
          onConfirm: () => {
            forceSyncSku(sku);
          },
        }),
      );
    },
    [dispatch, forceSyncSku],
  );

  const productColumns = useMemo(() => {
    return getProductsColumns(setViewedProduct, onClickForceSync);
  }, [onClickForceSync]);

  const retrieveData = async (pageNumber: number = 0, keyword: string | null = null) => {
    try {
      const params: any = {
        page: pageNumber,
        size: page.size,
        ...(keyword ? { keyword } : {}),
      };

      const res = await ProductsService.getAllProducts(params);
      dispatch(setProducts(res));
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve products. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const retrieveLatestSyncStatus = async () => {
    try {
      const status = await ProductsService.getLatestSyncStatus();
      dispatch(setProductSyncStatus(status));
      return status;
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve latest sync status. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
      return null;
    }
  };

  const onClickSyncLastModified = async () => {
    try {
      const status = await retrieveLatestSyncStatus();
      if (status?.status == 'INPROGRESS') {
        throw new Error('Syncing is still in progress. Please try again later. ');
      }
      const res = await ProductsService.syncLastModified();
      dispatch(
        showNotification({
          message: res.message,
        }),
      );
      await retrieveLatestSyncStatus();
    } catch (e: any) {
      console.log(e);
      dispatch(
        showNotification({
          message: `Failed to sync last modified. ${e}`,
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    retrieveData();
    retrieveLatestSyncStatus();
  }, []);

  useEffect(() => {
    if (syncStatus?.status !== 'INPROGRESS') return;

    const interval = setInterval(() => {
      retrieveLatestSyncStatus();
    }, 10000); // 30 seconds

    return () => clearInterval(interval);
  }, [syncStatus?.status]);

  return (
    <>
      <CommonDatagrid
        header={<p>Dear System Products</p>}
        headerActionButtons={
          <Box gap={2} display={'flex'} alignItems="flex-start">
            <SyncStatusCard
              onRefresh={() => {
                retrieveLatestSyncStatus();
              }}
              data={syncStatus}
            />
            <Button
              variant="contained"
              sx={{ fontSize: 12, fontWeight: 600 }}
              onClick={() => {
                onClickSyncLastModified();
              }}
            >
              Sync Last Modified
            </Button>
          </Box>
        }
        data={data}
        columns={productColumns}
        rowId={'productid'}
        searchbarPlaceholder={'Search product by name'}
        searchKey={'name'}
        serverPagination
        page={page.number}
        pageSize={page.size}
        rowCount={page.totalElements}
        onPageChange={(page: number) => {
          retrieveData(page);
        }}
        onChangeSearchKeyword={(keyword: string) => {
          retrieveData(0, keyword);
        }}
      />
      {viewedProduct ? (
        <CommonDialog
          isOpen={viewedProduct != null}
          header={
            <CommonDialogHeader
              title={`Viewing images of ${viewedProduct.name}`}
              onClose={() => {
                setViewedProduct(null);
              }}
            />
          }
          body={
            <>
              <Swiper
                modules={[Pagination]}
                spaceBetween={20}
                slidesPerView={3}
                pagination={{ clickable: true }}
                style={{ paddingBottom: 30 }}
              >
                {viewedProduct.attachments?.map((src: IProductAttachment, i) => (
                  <SwiperSlide key={i}>
                    <img
                      src={src.downloadUrl}
                      alt={`img-${i}`}
                      style={{ width: 80, height: 120 }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </>
          }
          footer={<></>}
        />
      ) : (
        <></>
      )}
    </>
  );
};
